import { Component, OnInit } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '@environments/environment';

interface AnalyticsSummaryPeriod {
  totalUsers: string;
  sessions: string;
  screenPageViews: string;
  bounceRate: string;
  averageSessionDuration: string;
}

interface AnalyticsChannel {
  sessionDefaultChannelGroup: string;
  totalUsers: string;
  sessions: string;
  bounceRate: string;
  averageSessionDuration: string;
}

interface AnalyticsPage {
  pagePath: string;
  screenPageViews: string;
  totalUsers: string;
  bounceRate: string;
}

interface AnalyticsFunnelStep {
  pagePath: string;
  screenPageViews: string;
  totalUsers: string;
}

interface AnalyticsDashboardResponse {
  dateRange: string;
  summary: {
    current: AnalyticsSummaryPeriod;
    prior: AnalyticsSummaryPeriod;
  };
  channels: AnalyticsChannel[];
  topPages: AnalyticsPage[];
  funnel: AnalyticsFunnelStep[];
  alerts: string[];
}

@Component({
  selector: 'app-analytics-dashboard',
  templateUrl: './analytics-dashboard.component.html',
  styleUrls: ['./analytics-dashboard.component.css'],
  standalone: false
})
export class AnalyticsDashboardComponent implements OnInit {

  loading = false;
  errorMessage: string | null = null;
  selectedDays = 7;
  data: AnalyticsDashboardResponse | null = null;

  // Funnel chart
  funnelChartData: any = {};
  funnelChartOptions: any = {};

  // Sorted channels
  sortedChannels: AnalyticsChannel[] = [];

  // Top pages (max 15)
  topPages: AnalyticsPage[] = [];

  readonly funnelLabels = [
    'Home (/)',
    'Search (/search)',
    'Book Form (/book-reservation)',
    'Confirmation'
  ];

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    this.loadData();
  }

  loadData(): void {
    this.loading = true;
    this.errorMessage = null;
    this.data = null;

    const params = new HttpParams().set('days', String(this.selectedDays));
    this.http.get<any>(`${environment.apiUrl}/analytics/dashboard`, { params }).subscribe(
      res => {
        // Support both wrapped { data: ... } and direct response shapes
        const payload: AnalyticsDashboardResponse = res.data ?? res;
        this.data = payload;
        this.sortedChannels = [...(payload.channels || [])].sort(
          (a, b) => Number(b.totalUsers) - Number(a.totalUsers)
        );
        this.topPages = (payload.topPages || []).slice(0, 15);
        this.buildFunnelChart(payload);
        this.loading = false;
      },
      err => {
        this.errorMessage = err?.error?.message || err?.message || 'Failed to load analytics data.';
        this.loading = false;
      }
    );
  }

  selectDays(days: number): void {
    if (this.selectedDays === days) return;
    this.selectedDays = days;
    this.loadData();
  }

  private buildFunnelChart(payload: AnalyticsDashboardResponse): void {
    const funnel = payload.funnel || [];

    // Map funnel steps by path to match the fixed label order
    const pathToUsers: Record<string, number> = {};
    funnel.forEach(step => { pathToUsers[step.pagePath] = Number(step.totalUsers) || 0; });

    const pathOrder = ['/', '/search', '/book-reservation', '/book-reservation-confirmation'];
    const userData = pathOrder.map(p => pathToUsers[p] || 0);

    this.funnelChartData = {
      labels: this.funnelLabels,
      datasets: [{
        label: 'Users',
        data: userData,
        backgroundColor: ['#1b3a5c', '#2563eb', '#0891b2', '#059669'],
        borderRadius: 4,
        borderSkipped: false
      }]
    };

    this.funnelChartOptions = {
      indexAxis: 'y',
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx: any) => ` ${Number(ctx.raw).toLocaleString()} users`
          }
        }
      },
      scales: {
        x: {
          beginAtZero: true,
          ticks: { callback: (v: any) => Number(v).toLocaleString() }
        },
        y: {
          ticks: { font: { size: 12 } }
        }
      }
    };
  }

  // ── formatting helpers ──────────────────────────────────────────────────────

  formatNumber(val: string | number): string {
    const n = Number(val);
    if (isNaN(n)) return '—';
    return n.toLocaleString();
  }

  formatPercent(val: string | number): string {
    const n = Number(val);
    if (isNaN(n)) return '—';
    return (n * 100).toFixed(1) + '%';
  }

  formatDuration(val: string | number): string {
    const secs = Number(val);
    if (isNaN(secs) || secs < 0) return '—';
    const m = Math.floor(secs / 60);
    const s = Math.round(secs % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  /** Returns a signed percentage change string, e.g. "+12.3%" or "-4.1%" */
  pctChange(current: string | number, prior: string | number): string {
    const c = Number(current);
    const p = Number(prior);
    if (isNaN(c) || isNaN(p) || p === 0) return '—';
    const pct = ((c - p) / p) * 100;
    const sign = pct >= 0 ? '+' : '';
    return `${sign}${pct.toFixed(1)}%`;
  }

  /** Positive if current > prior, except for bounceRate where lower is better */
  isPositiveChange(current: string | number, prior: string | number, lowerIsBetter = false): boolean {
    const c = Number(current);
    const p = Number(prior);
    if (isNaN(c) || isNaN(p)) return false;
    return lowerIsBetter ? c < p : c > p;
  }

  truncatePath(path: string, max = 60): string {
    if (!path) return '';
    if (path.length <= max) return path;
    return path.slice(0, max) + '…';
  }
}
